import { supabase } from '../../config/supabase';

/**
 * Get all conversations for current user
 */
export const getConversations = async (userId, companyId) => {
    try {
        const { data: conversations, error } = await supabase
            .from('conversations')
            .select(`
                *,
                participant1:users!conversations_participant1_id_fkey(id, username, full_name, avatar_url, role),
                participant2:users!conversations_participant2_id_fkey(id, username, full_name, avatar_url, role)
            `)
            .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
            // .eq('company_id', companyId)
            .order('updated_at', { ascending: false });

        if (error) throw error;

        // Calculate unread count and get last message for each conversation
        const conversationsWithUnread = await Promise.all(conversations.map(async (conv) => {
            // Get unread count
            const { count, error: countError } = await supabase
                .from('messages')
                .select('*', { count: 'exact', head: true })
                .eq('conversation_id', conv.id)
                .eq('is_read', false)
                .neq('sender_id', userId);

            if (countError) console.error('Error counting unread:', countError);

            // Get last message
            const { data: lastMessage, error: msgError } = await supabase
                .from('messages')
                .select('id, content, created_at, sender_id, is_read')
                .eq('conversation_id', conv.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            if (msgError && msgError.code !== 'PGRST116') {
                console.error('Error fetching last message:', msgError);
            }

            // Determine the other user
            const otherUser = conv.participant1_id === userId ? conv.participant2 : conv.participant1;

            return {
                ...conv,
                unreadCount: count || 0,
                lastMessage: lastMessage || null,
                otherUser
            };
        }));

        return conversationsWithUnread;
    } catch (error) {
        console.error('Error fetching conversations:', error);
        throw error;
    }
};

/**
 * Get or create a conversation between two users
 */
export const getOrCreateConversation = async (currentUserId, otherUserId, companyId) => {
    try {
        // Check if conversation already exists (in either direction)
        const { data: existing, error: searchError } = await supabase
            .from('conversations')
            .select(`
                *,
                participant1:users!conversations_participant1_id_fkey(id, username, full_name, avatar_url, role),
                participant2:users!conversations_participant2_id_fkey(id, username, full_name, avatar_url, role)
            `)
            .eq('company_id', companyId)
            .or(`and(participant1_id.eq.${currentUserId},participant2_id.eq.${otherUserId}),and(participant1_id.eq.${otherUserId},participant2_id.eq.${currentUserId})`)
            .single();

        if (searchError && searchError.code !== 'PGRST116') throw searchError;

        if (existing) {
            // Conversation exists
            const otherUser = existing.participant1_id === currentUserId ? existing.participant2 : existing.participant1;
            return { ...existing, otherUser };
        }

        // Create new conversation
        const { data: newConv, error: createError } = await supabase
            .from('conversations')
            .insert({
                company_id: companyId,
                participant1_id: currentUserId,
                participant2_id: otherUserId
            })
            .select(`
                *,
                participant1:users!conversations_participant1_id_fkey(id, username, full_name, avatar_url, role),
                participant2:users!conversations_participant2_id_fkey(id, username, full_name, avatar_url, role)
            `)
            .single();

        if (createError) throw createError;

        const otherUser = newConv.participant1_id === currentUserId ? newConv.participant2 : newConv.participant1;
        return { ...newConv, otherUser };
    } catch (error) {
        console.error('Error getting/creating conversation:', error);
        throw error;
    }
};

/**
 * Get messages for a conversation with pagination
 */
export const getMessages = async (conversationId, limit = 30, before = null) => {
    try {
        let query = supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', conversationId)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (before) {
            query = query.lt('created_at', before);
        }

        const { data: messages, error } = await query;

        if (error) throw error;

        // Reverse to show oldest first
        return messages.reverse();
    } catch (error) {
        console.error('Error fetching messages:', error);
        throw error;
    }
};

/**
 * Send a message
 */
export const sendMessage = async (conversationId, senderId, receiverId, content, attachments = null) => {
    try {
        const { data: message, error } = await supabase
            .from('messages')
            .insert({
                conversation_id: conversationId,
                sender_id: senderId,
                receiver_id: receiverId,
                content: content,
                attachments: attachments
            })
            .select()
            .single();

        if (error) throw error;

        // Update conversation's updated_at timestamp
        await supabase
            .from('conversations')
            .update({ updated_at: new Date().toISOString() })
            .eq('id', conversationId);

        return message;
    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (conversationId, userId) => {
    try {
        const { error } = await supabase
            .from('messages')
            .update({ is_read: true, read_at: new Date().toISOString() })
            .eq('conversation_id', conversationId)
            .eq('receiver_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error marking messages as read:', error);
        throw error;
    }
};

/**
 * Delete a conversation
 */
export const deleteConversation = async (conversationId) => {
    try {
        // Delete all messages first
        await supabase
            .from('messages')
            .delete()
            .eq('conversation_id', conversationId);

        // Delete conversation
        const { error } = await supabase
            .from('conversations')
            .delete()
            .eq('id', conversationId);

        if (error) throw error;

        return { success: true };
    } catch (error) {
        console.error('Error deleting conversation:', error);
        throw error;
    }
};

/**
 * Get users available for messaging (same company)
 */
export const getAvailableUsers = async (companyId, currentUserId) => {
    try {
        const { data: users, error } = await supabase
            .from('users')
            .select('id, username, full_name, avatar_url, role, status')
            .eq('company_id', companyId)
            .eq('status', 'active')
            .neq('id', currentUserId)
            .order('username');

        if (error) throw error;

        return users;
    } catch (error) {
        console.error('Error fetching available users:', error);
        throw error;
    }
};

/**
 * Get total unread message count
 */
export const getUnreadMessageCount = async (userId) => {
    try {
        const { count, error } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('receiver_id', userId)
            .eq('is_read', false);

        if (error) throw error;

        return count || 0;
    } catch (error) {
        console.error('Error fetching unread count:', error);
        return 0;
    }
};

/**
 * Subscribe to new messages
 */
export const subscribeToMessages = (userId, callback) => {
    const channelName = `messages-${userId}-${Date.now()}`;
    console.log('Creating message subscription channel:', channelName);

    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
                filter: `receiver_id=eq.${userId}`
            },
            (payload) => {
                console.log('Message subscription payload received:', payload);
                callback(payload.new);
            }
        )
        .subscribe((status) => {
            console.log('Message subscription status:', status);
        });

    return subscription;
};

/**
 * Subscribe to message read receipts
 */
export const subscribeToReadReceipts = (userId, callback) => {
    const channelName = `read-receipts-${userId}-${Date.now()}`;
    console.log('Creating read receipt subscription channel:', channelName);

    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'messages',
                filter: `sender_id=eq.${userId}`
            },
            (payload) => {
                console.log('Read receipt payload received:', payload);
                if (payload.new.is_read && !payload.old.is_read) {
                    callback(payload.new);
                }
            }
        )
        .subscribe((status) => {
            console.log('Read receipt subscription status:', status);
        });

    return subscription;
};

/**
 * Subscribe to messages in a specific conversation (for real-time updates)
 */
export const subscribeToConversationMessages = (conversationId, callback) => {
    const channelName = `conversation-${conversationId}-${Date.now()}`;
    console.log('Creating conversation message subscription channel:', channelName);

    const subscription = supabase
        .channel(channelName)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'messages',
                filter: `conversation_id=eq.${conversationId}`
            },
            (payload) => {
                console.log('Conversation message payload received:', payload);
                callback(payload.new);
            }
        )
        .subscribe((status) => {
            console.log('Conversation message subscription status:', status);
        });

    return subscription;
};
